import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { PlanService } from '../../services/plan.service';
import type { PlanDTO, PlanStatusEnumValue, FrequencyEnumValue, ListPlanFilterSpec } from '../../core/models';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';
import { PlanReadComponent } from './plan-read.component';

@Component({
  selector: 'app-plans-list',
  templateUrl: './plans-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249);
        color: rgb(15 23 42);
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th.p-sortable-column:focus-visible {
        outline: 2px solid rgb(14 165 233);
        outline-offset: -2px;
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255);
        color: rgb(3 105 161);
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .plans-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233);
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .plans-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .plans-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .plan-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .plan-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    DrawerModule,
    PlanReadComponent
  ]
})
export class PlansListComponent {
  private planService = inject(PlanService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  selectedPlan = signal<PlanDTO | null>(null);
  isDetailsOpen = signal(false);
  detailsLoading = signal(false);

  deleteModalOpen = signal(false);
  deleteModalShown = signal(false);
  planToDelete = signal<PlanDTO | null>(null);
  deleteLoading = signal(false);

  plans = signal<PlanDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  sortField = signal('createdAt');
  sortOrder = signal(-1);

  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    q: [''],
    status: ['ACTIVE' as PlanStatusEnumValue]
  });

  hasActiveFilters = signal(false);

  statusOptions: { value: PlanStatusEnumValue; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'ARCHIVED', label: 'Archivado' }
  ];

  statusFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    ...this.statusOptions
  ];

  frequencyOptions: { value: FrequencyEnumValue; label: string }[] = [
    { value: 'ONCE', label: 'Una vez' },
    { value: 'DAILY', label: 'Diario' },
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'BIWEEKLY', label: 'Quincenal' },
    { value: 'MONTHLY', label: 'Mensual' },
    { value: 'QUARTERLY', label: 'Trimestral' },
    { value: 'SEMIANNUALLY', label: 'Semestral' },
    { value: 'ANNUALLY', label: 'Anual' }
  ];

  constructor() {
    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.status.valueChanges.pipe(
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.loadPlans();
  }

  loadPlans(): void {
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    const pageable = {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    };

    this.planService.listPlans(spec, pageable).subscribe({
      next: (response) => {
        if (response.ok) {
          this.plans.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems ?? 0);
            this.totalPages.set(response.page.totalPages ?? 0);
            this.pageSize.set(response.page.pageSize ?? 20);
            this.currentPage.set(Math.max(0, (response.page.currentPage ?? 1) - 1));
          } else {
            this.totalItems.set(response.body?.length ?? 0);
          }
        } else {
          this.error.set(response.message || 'Error al cargar planes');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set('Error al conectar con el servidor.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
        console.error('Error loading plans:', err);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged =
      nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged
      ? 0
      : event.rows
        ? Math.floor((event.first ?? 0) / event.rows)
        : 0;

    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) return;

    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadPlans();
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadPlans();
  }

  clearFilters(): void {
    this.filtersForm.reset(
      { q: '', status: 'ACTIVE' },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(raw.q?.trim() || raw.status !== 'ACTIVE')
    );
  }

  private isSpecificFilter(value: string | null | undefined): boolean {
    return !!value && value !== this.allFilterValue;
  }

  openPlanDetails(plan: PlanDTO): void {
    if (!plan?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedPlan.set(null);
    this.detailsLoading.set(true);
    this.planService.getPlanById(plan.id).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedPlan.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeDetails();
        }
        this.detailsLoading.set(false);
      },
      error: () => {
        this.detailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle del plan');
        this.closeDetails();
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedPlan.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  editPlan(plan: PlanDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!plan?.id) return;
    this.router.navigate(['/plans', plan.id]);
  }

  editFromDrawer(): void {
    const plan = this.selectedPlan();
    if (!plan?.id) return;
    this.router.navigate(['/plans', plan.id]);
  }

  openDeleteModal(plan: PlanDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!plan?.id) return;
    this.planToDelete.set(plan);
    this.deleteModalOpen.set(true);
    setTimeout(() => this.deleteModalShown.set(true), 10);
  }

  closeDeleteModal(): void {
    this.deleteModalShown.set(false);
    setTimeout(() => {
      this.deleteModalOpen.set(false);
      this.planToDelete.set(null);
    }, 200);
  }

  confirmDeletePlan(): void {
    const plan = this.planToDelete();
    if (!plan?.id) return;
    this.deleteLoading.set(true);
    this.planService.deletePlan(plan.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.toastr.success('Plan eliminado');
          this.loadPlans();
          this.closeDeleteModal();
        } else {
          this.toastr.error(response.message || 'Error al eliminar el plan');
        }
        this.deleteLoading.set(false);
      },
      error: (err) => {
        this.toastr.error(err.error?.message || 'Error al eliminar el plan');
        this.deleteLoading.set(false);
      }
    });
  }

  getStatusLabel(status: PlanStatusEnumValue | string | undefined): string {
    if (!status) return '—';
    return this.statusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getStatusSeverity(status: PlanStatusEnumValue | string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'DRAFT': return 'info';
      case 'ARCHIVED': return 'secondary';
      default: return 'secondary';
    }
  }

  getStatusIcon(status: PlanStatusEnumValue | string | undefined): string {
    switch (status) {
      case 'DRAFT': return 'pi pi-file-edit';
      case 'ACTIVE': return 'pi pi-check-circle';
      case 'ARCHIVED': return 'pi pi-inbox';
      default: return 'pi pi-file';
    }
  }

  getStatusIconClasses(status: PlanStatusEnumValue | string | undefined): string {
    const base = 'shrink-0 h-10 w-10 rounded-full flex items-center justify-center';
    switch (status) {
      case 'DRAFT': return `${base} bg-sky-100 text-sky-600`;
      case 'ACTIVE': return `${base} bg-emerald-100 text-emerald-600`;
      case 'ARCHIVED': return `${base} bg-slate-200 text-slate-600`;
      default: return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getFrequencyLabel(freq: FrequencyEnumValue | string | undefined): string {
    if (!freq) return '—';
    return this.frequencyOptions.find((o) => o.value === freq)?.label ?? String(freq);
  }

  private buildSpecFromFilters(): ListPlanFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: ListPlanFilterSpec = {};
    const q = value.q?.trim();
    if (q) {
      spec['q[lk]'] = q;
    }
    if (this.isSpecificFilter(value.status)) {
      spec.status = value.status;
    }
    return spec;
  }
}
